import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp, X } from "lucide-react";
import { cn } from "../../../utils";
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;
const SelectTrigger = /*#__PURE__*/ React.forwardRef(({ className, children, allowClear, onClear, hasValue, ...props }, ref)=>{
    const handleClearClick = (e)=>{
        e.stopPropagation();
        e.preventDefault();
        onClear?.();
    };
    const handleClearKeyDown = (e)=>{
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            onClear?.();
        }
    };
    return /*#__PURE__*/ _jsxs(SelectPrimitive.Trigger, {
        ref: ref,
        className: cn("flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background data-placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1", className),
        ...props,
        children: [
            children,
            /*#__PURE__*/ _jsxs("div", {
                className: "ml-2 flex items-center gap-1 shrink-0",
                children: [
                    allowClear && hasValue && /*#__PURE__*/ _jsx("span", {
                        role: "button",
                        tabIndex: 0,
                        "aria-label": "Clear selection",
                        onPointerDown: (e)=>e.stopPropagation(),
                        onClick: handleClearClick,
                        onKeyDown: handleClearKeyDown,
                        className: "inline-flex items-center justify-center rounded-xs focus:outline-hidden focus:ring-1 focus:ring-ring cursor-pointer",
                        children: /*#__PURE__*/ _jsx(X, {
                            className: "h-4 w-4 opacity-50 hover:opacity-100 transition-opacity"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SelectPrimitive.Icon, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsx(ChevronDown, {
                            className: "h-4 w-4 opacity-50"
                        })
                    })
                ]
            })
        ]
    });
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;
const SelectScrollUpButton = /*#__PURE__*/ React.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ _jsx(SelectPrimitive.ScrollUpButton, {
        ref: ref,
        className: cn("flex cursor-default items-center justify-center py-1", className),
        ...props,
        children: /*#__PURE__*/ _jsx(ChevronUp, {
            className: "h-4 w-4"
        })
    }));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;
const SelectScrollDownButton = /*#__PURE__*/ React.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ _jsx(SelectPrimitive.ScrollDownButton, {
        ref: ref,
        className: cn("flex cursor-default items-center justify-center py-1", className),
        ...props,
        children: /*#__PURE__*/ _jsx(ChevronDown, {
            className: "h-4 w-4"
        })
    }));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;
const SelectContent = /*#__PURE__*/ React.forwardRef(({ className, children, position = "popper", ...props }, ref)=>/*#__PURE__*/ _jsx(SelectPrimitive.Portal, {
        children: /*#__PURE__*/ _jsxs(SelectPrimitive.Content, {
            ref: ref,
            className: cn("relative z-(--z-dropdown) max-h-(--radix-select-content-available-height) min-w-32 overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-select-content-transform-origin)", position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1", className),
            position: position,
            ...props,
            children: [
                /*#__PURE__*/ _jsx(SelectScrollUpButton, {}),
                /*#__PURE__*/ _jsx(SelectPrimitive.Viewport, {
                    className: cn("p-1", position === "popper" && "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)"),
                    children: children
                }),
                /*#__PURE__*/ _jsx(SelectScrollDownButton, {})
            ]
        })
    }));
SelectContent.displayName = SelectPrimitive.Content.displayName;
const SelectLabel = /*#__PURE__*/ React.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ _jsx(SelectPrimitive.Label, {
        ref: ref,
        className: cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className),
        ...props
    }));
SelectLabel.displayName = SelectPrimitive.Label.displayName;
const SelectItem = /*#__PURE__*/ React.forwardRef(({ className, children, indicatorSide = "left", ...props }, ref)=>/*#__PURE__*/ _jsxs(SelectPrimitive.Item, {
        ref: ref,
        className: cn("relative flex w-full cursor-default select-none items-center rounded-xs py-1.5 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50", indicatorSide === "left" ? "pl-8 pr-2" : "pl-3 pr-8", className),
        ...props,
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: cn("absolute flex h-3.5 w-3.5 items-center justify-center", indicatorSide === "left" ? "left-2" : "right-2"),
                children: /*#__PURE__*/ _jsx(SelectPrimitive.ItemIndicator, {
                    children: /*#__PURE__*/ _jsx(Check, {
                        className: "h-4 w-4"
                    })
                })
            }),
            /*#__PURE__*/ _jsx(SelectPrimitive.ItemText, {
                children: children
            })
        ]
    }));
SelectItem.displayName = SelectPrimitive.Item.displayName;
const SelectSeparator = /*#__PURE__*/ React.forwardRef(({ className, ...props }, ref)=>/*#__PURE__*/ _jsx(SelectPrimitive.Separator, {
        ref: ref,
        className: cn("-mx-1 my-1 h-px bg-muted", className),
        ...props
    }));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator, SelectScrollUpButton, SelectScrollDownButton };

//# sourceMappingURL=select.js.map